<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useQuery } from '@vue/apollo-composable'
import {
  CheckDialogDataQuery,
  CheckDialogDataQueryVariables,
  ConfirmCheck,
  FileUploadCheck,
  NumericCheck,
  StringCheck,
} from '@/generated/graphql'
import gql from 'graphql-tag'
import { computed } from 'vue'
import DateTime from '@/components/DateTime.vue'
import Markdown from '@/components/Markdown.vue'
import IconChecked from '@/components/icons/IconChecked.vue'
import InlineBreadcrumb from '@/components/InlineBreadcrumb.vue'
import MilliQuantity from '@/components/MilliQuantity.vue'

const model = defineModel<string>({ required: false })

const { t } = useI18n()

type LocalCheck = CheckDialogDataQuery['shopFloor']['check']
type LocalCheckI = LocalCheck

const fetchQuery = useQuery<CheckDialogDataQuery, CheckDialogDataQueryVariables>(
  gql`
    query CheckDialogData($id: ID!) {
      shopFloor {
        check(id: $id) {
          __typename
          ... on CheckI {
            id
            released
            reportingUser {
              id
              firstname
              lastname
            }
            configuration {
              id
              description
              isRequired
              lowerLimit
              upperLimit
              bop {
                id
                version
                productConfiguration {
                  id
                  name
                  product {
                    id
                    name
                  }
                }
                site {
                  id
                  name
                }
              }
              module {
                id
                name
              }
              step {
                id
                name
              }
            }
            serialNumber
            poNumber
            createdAt
            updatedAt
          }
          ... on ConfirmCheck {
            confirmed
          }
          ... on NumericCheck {
            milliQuantity
          }
          ... on StringCheck {
            text
          }
          ... on FileUploadCheck {
            upload {
              id
              name
              url
            }
          }
        }
      }
    }
  `,
  () => ({
    id: model.value as string,
  }),
  () => ({
    enabled: !!model.value,
  }),
)
const check = computed<LocalCheckI | undefined>(() => fetchQuery.result.value?.shopFloor.check)
const generic = computed<LocalCheckI | undefined>(() => check.value)
const numeric = computed(() =>
  generic.value?.__typename == 'NumericCheck' ? (generic.value as NumericCheck) : undefined,
)
const confirm = computed(() =>
  generic.value?.__typename == 'ConfirmCheck' ? (generic.value as ConfirmCheck) : undefined,
)
const string = computed(() =>
  generic.value?.__typename == 'StringCheck' ? (generic.value as StringCheck) : undefined,
)
const fileUpload = computed(() =>
  generic.value?.__typename == 'FileUploadCheck' ? (generic.value as FileUploadCheck) : undefined,
)
</script>

<template>
  <v-dialog
    v-if="generic"
    :model-value="!!model"
    width="500"
    v-bind="$attrs"
    scrollable
    @update:model-value="model = undefined"
  >
    <v-card :loading="fetchQuery.loading.value">
      <v-card-title>
        <v-btn
          icon="close"
          variant="flat"
          density="compact"
          class="float-right"
          @click="model = undefined"
        />
      </v-card-title>
      <v-card-text>
        <dl>
          <dd>{{ t('entity.check.field.poNumber') }}</dd>
          <dt>{{ generic.poNumber }}</dt>
          <dd>{{ t('entity.check.field.serialNumber') }}</dd>
          <dt>{{ generic.serialNumber }}</dt>
          <dd>{{ t('entity.step.singular') }}</dd>
          <dt><inline-breadcrumb :breadcrumb="generic.configuration" /></dt>
          <dd>{{ t('entity.checkConfiguration.field.description') }}</dd>
          <dt>{{ generic.configuration.description }}</dt>
          <dd>{{ t('entity.checkConfiguration.field.isRequired') }}</dd>
          <dt><icon-checked :value="generic.configuration.isRequired" /></dt>
          <dd>{{ t('entity.check.field.released') }}</dd>
          <dt><icon-checked :value="generic.released" /></dt>
          <dd>{{ t('entity.check.field.createdAt') }}</dd>
          <dt><date-time :model-value="generic.createdAt" /></dt>
          <dd>{{ t('entity.check.field.updatedAt') }}</dd>
          <dt><date-time :model-value="generic.updatedAt" /></dt>
          <dd>{{ t('entity.check.field.reportingUser') }}</dd>
          <dt>{{ generic.reportingUser.firstname }} {{ generic.reportingUser.lastname }}</dt>
          <template v-if="confirm">
            <dd>{{ t('entity.check.field.confirmed') }}</dd>
            <dt><icon-checked :value="confirm.confirmed" /></dt>
          </template>
          <template v-if="string">
            <dd>{{ t('entity.check.field.text') }}</dd>
            <dt><markdown :text="string.text" /></dt>
          </template>
          <template v-if="numeric">
            <dd>{{ t('entity.check.field.milliQuantity') }}</dd>
            <dt><milli-quantity :milli-quantity="numeric.milliQuantity" /></dt>
            <dd>{{ t('entity.checkConfiguration.field.lowerLimit') }}</dd>
            <dt><milli-quantity :milli-quantity="generic.configuration.lowerLimit" /></dt>
            <dd>{{ t('entity.checkConfiguration.field.upperLimit') }}</dd>
            <dt><milli-quantity :milli-quantity="generic.configuration.upperLimit" /></dt>
          </template>
          <template v-if="fileUpload">
            <dd>{{ t('entity.check.field.upload') }}</dd>
            <dt>
              <v-btn
                variant="text"
                size="small"
                prepend-icon="attach_file"
                :href="fileUpload.upload.url"
                target="_blank"
              >
                {{ fileUpload.upload.name }}
              </v-btn>
            </dt>
          </template>
        </dl>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn variant="elevated" color="primary" @click="model = undefined">
          {{ t(`button.close`) }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped lang="scss">
dd {
  font-size: 0.8em;
  margin-top: 0.5em;
  color: #888;
}
</style>
